import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { Event } from '@patsnap/tahiti-core/common/event';
import { SpecificationConfirmDisclosureRepository } from '../specification-confirm-disclosure-repository';
import { SpecificationConfirmDisclosureModel } from '../../models/specification-confirm-disclosure-model';
import type { SpecificationConfirmDisclosureService } from '../specification-confirm-disclosure-service';
import type {
    SpecificationConfirmDisclosureData,
    SpecificationConfirmDisclosureState,
    SpecificationConfirmDisclosureDataErrors,
} from '../../types/specification-confirm-disclosure';
import {
    SPECIFICATION_TECHNICAL_QUESTION_MAX_LENGTH,
    SPECIFICATION_TECHNICAL_SOLUTION_MAX_LENGTH,
    SPECIFICATION_TECHNICAL_BENEFIT_MAX_LENGTH,
} from '../../constants/specification-confirm-disclosure';
import { Disposable } from '@patsnap/tahiti-core';
import type { SpecificationStreamContent } from '../../types/specification';
import _ from 'lodash';
import { SpecificationService } from '../specification-service';
import { SpecificationStep } from '../../enums';
import { CheckSensitiveWordService } from '@/services/check-sensitive-word/check-sensitive-word';
import wordsCount from 'words-count';

const SPECIFICATION_STEP_STORAGE_KEY = 'specification_current_step';

@injectable()
export class SpecificationConfirmDisclosureServiceImpl implements SpecificationConfirmDisclosureService {
    private _disposables: Disposable[] = [];

    constructor(
        @inject(SpecificationConfirmDisclosureRepository)
        private _repository: SpecificationConfirmDisclosureRepository,
        @inject(SpecificationConfirmDisclosureModel)
        private _model: SpecificationConfirmDisclosureModel,
        @inject(SpecificationService)
        private _specificationService: SpecificationService,
        @inject(CheckSensitiveWordService)
        private _checkSensitiveWordService: CheckSensitiveWordService,
    ) {}

    get onStateChange(): Event<SpecificationConfirmDisclosureState> {
        return this._model.onStatesChange;
    }

    private get _currentTaskId(): string | null {
        return this._model.modelData.taskId;
    }

    async init(taskId: string, regenerate?: boolean): Promise<void> {
        if (this._currentTaskId === taskId && !regenerate) {
            return;
        }

        this._model.update(state => ({
            ...state,
            taskId,
            loading: true,
        }));

        await this._initializeFormData(taskId, regenerate);
        this._model.update(state => ({
            ...state,
            loading: false,
        }));
    }

    private async _initializeFormData(taskId: string, regenerate?: boolean): Promise<any[]> {
        this._disposeAll();

        const providers = this._getProviders(taskId, regenerate);
        const deferrers = await this._subscribeToDeferrers(providers);

        return Promise.allSettled(deferrers.map(deferred => deferred.promise));
    }

    private _getProviders(taskId: string, regenerate?: boolean) {
        return {
            classification: this._repository.genClassification(taskId, regenerate),
            statutoryCategory: this._repository.genStatutoryCategory(taskId, regenerate),
            technical: this._repository.genTechnical(taskId, regenerate),
            embodiment: this._repository.genEmbodiment(taskId, regenerate),
        };
    }

    private async _subscribeToDeferrers(providers: ReturnType<typeof this._getProviders>) {
        const deferrers = [
            this._subscribeToClassification(providers.classification),
            this._subscribeToStatutoryCategory(providers.statutoryCategory),
            this._subscribeToTechnical(providers.technical),
            this._subscribeToEmbodiment(providers.embodiment),
        ];

        return deferrers;
    }

    private _subscribeToClassification(provider: ReturnType<typeof this._repository.genClassification>) {
        const deferred = provider.subscribe((classification: SpecificationStreamContent<string[]>) => {
            this._model.update(state => ({
                ...state,
                formData: {
                    ...state.formData,
                    classification,
                },
            }));
        });
        this._disposables.push(Disposable.create(provider.abort));
        return deferred;
    }

    private _subscribeToStatutoryCategory(provider: ReturnType<typeof this._repository.genStatutoryCategory>) {
        const deferred = provider.subscribe((statutoryCategory: SpecificationStreamContent<string>) => {
            this._model.update(state => ({
                ...state,
                formData: {
                    ...state.formData,
                    statutoryCategory,
                },
            }));
        });
        this._disposables.push(Disposable.create(provider.abort));
        return deferred;
    }

    private _selectAllEmbodiments(data: { text: string; selected: boolean }[]) {
        const selectedEmbodiments = _.map(data, item => ({
            ...item,
            selected: true,
        }));
        return selectedEmbodiments;
    }

    private _subscribeToEmbodiment(provider: ReturnType<typeof this._repository.genEmbodiment>) {
        const deferred = provider.subscribe((embodiments: SpecificationStreamContent<{
            text: string;
            selected: boolean;
        }[]>) => {
            this._model.update(state => {
                state.formData.embodiments = {
                    content: this._selectAllEmbodiments(Array.isArray(embodiments?.content) ? embodiments.content : []),
                    status: embodiments.status,
                };
                return state;
            });
        });
        this._disposables.push(Disposable.create(provider.abort));
        return deferred;
    }

    private _subscribeToTechnical(provider: ReturnType<typeof this._repository.genTechnical>) {
        const deferred = provider.subscribe((technical: SpecificationStreamContent<{
            technical_problem: string;
            technical_methods: string;
            benefit: string;
        }>) => {
            this._model.update(state => ({
                ...state,
                formData: {
                    ...state.formData,
                    technicalQuestion: {
                        content: technical.content?.technical_problem,
                        status: technical.status,
                    },
                    technicalSolution: {
                        content: technical.content?.technical_methods,
                        status: technical.status,
                    },
                    technicalBenefit: {
                        content: technical.content?.benefit,
                        status: technical.status,
                    },
                },
            }));
        });
        this._disposables.push(Disposable.create(provider.abort));
        return deferred;
    }

    private _disposeAll(): void {
        this._disposables.forEach(disposable => disposable.dispose());
        this._disposables = [];
    }

    getState(): SpecificationConfirmDisclosureState {
        return this._model.modelData;
    }

    patchFormData(payload: Partial<SpecificationConfirmDisclosureData>): void {
        this._model.update(state => ({
            ...state,
            // 数组类型需要特殊处理，直接覆盖
            formData: _.mergeWith({}, state.formData, payload, (objValue, srcValue) => {
                if (_.isArray(objValue) || _.isArray(srcValue)) {
                    return srcValue;
                }
            }),
        }));
    }
    async validateFormData(): Promise<SpecificationConfirmDisclosureDataErrors> {
        const { formData } = this.getState();
        const errors: SpecificationConfirmDisclosureDataErrors = await this.validate4formData(formData);

        this._model.update(state => ({
            ...state,
            formErrors: errors,
        }));

        return errors;
    }

    async validate4formData(formData: SpecificationConfirmDisclosureData): Promise<SpecificationConfirmDisclosureDataErrors> {
        const errors: SpecificationConfirmDisclosureDataErrors = {};
        this._validateClassification(formData, errors);
        this._validateStatutoryCategory(formData, errors);
        this._validateTechnicalFields(formData, errors);
        return errors;
    }

    private _validateClassification(
        formData: SpecificationConfirmDisclosureData,
        errors: SpecificationConfirmDisclosureDataErrors,
    ): void {
        if (!formData.classification.content || !formData.classification.content.length) {
            errors.classification = '';
        }
    }

    private _validateStatutoryCategory(
        formData: SpecificationConfirmDisclosureData,
        errors: SpecificationConfirmDisclosureDataErrors,
    ): void {
        if (!formData.statutoryCategory.content) {
            errors.statutoryCategory = '';
        }
    }

    private _validateTechnicalFields(
        formData: SpecificationConfirmDisclosureData,
        errors: SpecificationConfirmDisclosureDataErrors,
    ): void {
        this._validateTechnicalField(
            formData.technicalQuestion.content,
            SPECIFICATION_TECHNICAL_QUESTION_MAX_LENGTH,
            'technicalQuestion',
            errors,
        );

        this._validateTechnicalField(
            formData.technicalSolution.content,
            SPECIFICATION_TECHNICAL_SOLUTION_MAX_LENGTH,
            'technicalSolution',
            errors,
        );

        this._validateTechnicalField(
            formData.technicalBenefit.content,
            SPECIFICATION_TECHNICAL_BENEFIT_MAX_LENGTH,
            'technicalBenefit',
            errors,
        );

        //FIXME: 验证实施例
    }

    private _validateTechnicalField(
        content: string | undefined,
        maxLength: number,
        field: keyof SpecificationConfirmDisclosureDataErrors,
        errors: SpecificationConfirmDisclosureDataErrors,
    ): void {
        if (!content) {
            errors[field] = '';
        } else {
            // 使用单词计数代替字符长度
            const wordCounts = wordsCount(content);
            if (wordCounts > maxLength) {
                errors[field] = '';
            }
        }
    }

    getFormErrors(): SpecificationConfirmDisclosureDataErrors {
        return this.getState().formErrors;
    }

    async submit(switchStep = true): Promise<void> {
        try {
            this._model.update(state => ({
                ...state,
                loadingSubmit: true,
            }));

            const errors = await this.validateFormData();
            if (Object.keys(errors).length > 0) {
                return;
            }

            const { taskId, formData } = this.getState();
            await this._checkSensitiveWordService.checkSensitiveWord(`${formData.technicalQuestion.content!}\n${formData.technicalSolution.content!}\n${formData.technicalBenefit.content!}`);
            await this._repository.submit(taskId, {
                classification: formData.classification.content!,
                statutoryCategory: formData.statutoryCategory.content!,
                technicalQuestion: formData.technicalQuestion.content!,
                technicalSolution: formData.technicalSolution.content!,
                technicalBenefit: formData.technicalBenefit.content!,
                embodiments: formData.embodiments.content!,
            });

            if (switchStep) {
                // 清除 storage 中的临时步骤
                if (typeof window !== 'undefined' && window.localStorage) {
                    window.localStorage.removeItem(SPECIFICATION_STEP_STORAGE_KEY);
                }
                // 切换到确认权利要求页面
                this._specificationService.switchStep(SpecificationStep.ConfirmClaims);
            }
        } finally {
            this._model.update(state => ({
                ...state,
                loadingSubmit: false,
            }));
        }
    }

    async regenerate(): Promise<void> {
        const { taskId } = this.getState();
        this._disposeAll();

        // 重置错误状态
        this._model.update(state => ({
            ...state,
            formErrors: {},
        }));

        // 等待取消结束
        await new Promise(resolve => setTimeout(resolve, 0));
        await this.init(taskId, true);
    }
}
